import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, Overline } from "@/components/motion";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import QuoteDialog from "@/components/QuoteDialog";
import { PRODUCTS } from "@/data/products";
import { breadcrumbSchema, absoluteUrl } from "@/lib/seo";

const crumbs = [
  { name: "Home", path: "/" },
  { name: "Products", path: "/products" },
];

const itemListSchema = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  itemListElement: PRODUCTS.map((p, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: p.name,
    url: absoluteUrl(`/products/${p.slug}`),
  })),
};

export default function Products() {
  return (
    <div>
      <Seo
        title="Bulk Millet Exporter India | Bajra, Wheat & Sesbania Seed Supplier"
        description="SDRC Global supplies bulk machine-clean millet, bajra (pearl millet), wheat and sesbania seed from India for global buyers, feed processors and agri-trading businesses."
        path="/products"
        jsonLd={[breadcrumbSchema(crumbs), itemListSchema]}
      />

      <section className="pt-36 md:pt-44 pb-14 md:pb-20 bg-brand-green-dark" data-testid="page-hero">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal><Breadcrumbs items={crumbs} className="mb-6" /></Reveal>
          <Reveal delay={0.03}><Overline className="text-brand-gold-light mb-5">Our Products</Overline></Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tight text-brand-cream leading-none">
              Millet &amp; grains, refined for the world.
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-lg text-brand-cream/75 max-w-2xl">
              Machine clean millet, bajra (pearl millet), wheat and sesbania seed — each processed to consistent, export-ready standards for international buyers.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-brand-cream py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {PRODUCTS.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 4) * 0.08}>
                <article className="group bg-white border border-brand-line hover:border-brand-gold transition-colors duration-500 h-full flex flex-col" data-testid={`product-card-${p.slug}`}>
                  <Link to={`/products/${p.slug}`} className="overflow-hidden aspect-[4/5] block" aria-label={`View ${p.name} details`}>
                    <img src={p.image} alt={p.imageAlt || p.name} loading="lazy" decoding="async" sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </Link>
                  <div className="p-6 flex flex-col flex-1">
                    <span className="text-[11px] uppercase tracking-[0.18em] text-brand-gold-dark font-semibold">{p.tagline}</span>
                    <h2 className="font-display text-2xl font-medium text-brand-green mt-2">
                      <Link to={`/products/${p.slug}`} className="hover:text-brand-gold-dark transition-colors">{p.name}</Link>
                    </h2>
                    <p className="text-sm text-brand-muted mt-3 leading-relaxed flex-1">{p.short}</p>
                    <div className="flex items-center justify-between mt-5 pt-5 border-t border-brand-line">
                      <Link to={`/products/${p.slug}`} data-testid={`view-${p.slug}`} className="text-sm font-semibold text-brand-green hover:text-brand-gold-dark transition-colors inline-flex items-center gap-1.5">
                        View details <ArrowRight size={14} />
                      </Link>
                      <QuoteDialog product={p.name}>
                        <button data-testid={`card-quote-${p.slug}`} className="text-sm text-brand-muted hover:text-brand-green transition-colors">Quote</button>
                      </QuoteDialog>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
